import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { I18NextModule } from 'angular-i18next';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterModule } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { StorageService } from 'src/app/core/storage/storage.service';
import {
  Collective,
  Country,
  State,
  User
} from 'src/app/shared/model/user.model';
import {
  COUNTRIES,
  STATES
} from 'src/app/shared/constants/user-data.constants';
import { UserService } from 'src/app/core/services/user.service';
import { FeedbackModalComponent } from 'src/app/shared/components/feedback-modal/feedback-modal.component';
import i18next from 'i18next';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ModalConfirmRequestComponent } from 'src/app/shared/components/modal-confirm-request/modal-confirm-request.component';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  standalone: true,
  selector: 'app-manage-data',
  templateUrl: './manage-data.component.html',
  styleUrls: ['./manage-data.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    I18NextModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule
  ]
})
export class ManageDataComponent implements OnInit {
  form: FormGroup = this.fb.group({});
  user!: User;
  states: State[] = [];
  countries: Country[] = [];
  spinner = false;
  collective = Collective;

  constructor(
    private fb: FormBuilder,
    private storageService: StorageService,
    private userService: UserService,
    public dialog: MatDialog,
    private location: Location
  ) {
    this.form = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+[.]+[a-zA-Z]{2,4}$')
        ]
      ],
      phone: ['', [Validators.required]],
      country: [''],
      city: ['', [Validators.required]],
      postalCode: [''],
      state: ['', [Validators.required]],
      roadType: ['', [Validators.required]],
      street: ['', [Validators.required]],
      number: [''],
      complement: [''],
      commercialCommunicationsConsent: [false],
      privacyPolicyAccepted: [false]
    });
  }

  ngOnInit(): void {
    this.user = this.storageService.get('config');
    this.states = STATES;
    this.countries = COUNTRIES;

    this.form.get('email')?.setValue(this.user.email);
    this.form
      .get('phone')
      ?.setValue(
        this.user.groupType === this.collective.EXTERNVO || this.user.groupType === this.collective.RETIREE
          ? this.user.contact?.mobileNumber
          : this.user.contact?.leasingNumber
      );
    this.form.get('country')?.setValue(this.user.address?.countryCode);
    this.form.get('city')?.setValue(this.user.address?.city);
    this.form.get('postalCode')?.setValue(this.user.address?.postalCode);
    this.form.get('state')?.setValue(this.user.address?.stateCode);
    this.form.get('roadType')?.setValue(this.user.address?.roadType);
    this.form.get('street')?.setValue(this.user.address?.street);
    this.form.get('number')?.setValue(this.user.address?.roadNumber);
    this.form.get('complement')?.setValue(this.user.address?.roadComplement);
    this.form
      .get('commercialCommunicationsConsent')
      ?.setValue(this.user?.commercialCommunicationsConsent ?? false);
    this.form
      .get('privacyPolicyAccepted')
      ?.setValue(this.user?.privacyPolicyAccepted ?? false);

    if (this.user?.groupType === this.collective.EXTERNVO || this.user?.groupType === this.collective.RETIREE) {
      this.form
        .get('privacyPolicyAccepted')
        ?.setValidators([Validators.requiredTrue]);
      this.form.get('privacyPolicyAccepted')?.updateValueAndValidity();
    }
  }

  updateUser() {
    this.spinner = true;
    const body = {
      user: {
        contact: {
          leasingNumber: this.form.get('phone')?.value
        },
        email: this.form.get('email')?.value,
        address: {
          city: this.form.get('city')?.value,
          countryCode: this.form.get('country')?.value,
          postalCode: this.form.get('postalCode')?.value,
          stateCode: this.form.get('state')?.value,
          street: this.form.get('street')?.value,
          roadType: this.form.get('roadType')?.value,
          roadNumber: this.form.get('number')?.value,
          roadComplement: this.form.get('complement')?.value
        }
      }
    };

    if (this.user?.groupType === this.collective.EXTERNVO || this.user?.groupType === this.collective.RETIREE) {
      (body.user as any).commercialCommunicationsConsent = this.form.get(
        'commercialCommunicationsConsent'
      )?.value;
      (body.user as any).privacyPolicyAccepted = this.form.get(
        'privacyPolicyAccepted'
      )?.value;
    }

    this.userService.updateUser(body).subscribe({
      next: () => {
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('dataUpdated'),
            redirect: true,
            list: true,
            manageData: true,
            theme: 'ligth'
          }
        });
        this.spinner = false;
      },
      error: () => {
        this.spinner = false;
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('dataUpdatedKO'),
            close: true,
            theme: 'ligth'
          }
        });
      }
    });
  }

  goBack() {
    this.location.back();
  }

  onCommercialConsentChange(event: MatCheckboxChange): void {
    const control = this.form.get('commercialCommunicationsConsent');

    if (!event.checked) {
      return;
    }

    const dialogRef = this.dialog.open(ModalConfirmRequestComponent, {
      disableClose: true,
      data: {
        htmlKey: 'voCommercialConsentModalHtml',
        confirmText: 'buttonConfirm',
        hideCancelButton: true
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        control?.setValue(false, { emitEvent: false });
      }
    });
  }
}
